<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0

@component
Floating local media player for a transcription job. The original media file is
used only as a browser File object and is never uploaded again or persisted.
-->

<script>
import { createEventDispatcher, onDestroy, onMount } from 'svelte';
import {
  getMediaFileMismatch,
  getTranscriptionMediaFile,
  setTranscriptionMediaFile,
} from '../../libs/TranscriptionAPI/TranscriptionMediaFileStore.js';
import uiStore from '../../utils/UIStore';

export let job;
export let seekRequest = null;
export let initialTime = 0;
export let initialVolume = 1;
export let initialMuted = false;
export let initialPosition = { x: 96, y: 120 };
export let initialSize = null;

const dispatch = createEventDispatcher();

let uiTexts;
uiStore.textStore.subscribe((store) => {
  uiTexts = store.texts;
});

$: mediaPlayerTexts = uiTexts?.transcription?.mediaPlayer ?? {};

let mediaFile = null;
let mediaSourceUrl = '';
let mediaElement;
let playerElement;
let fileInput;
let mismatch = '';
let mediaErrorMessage = '';
let lastSeekRequestId = null;
let initialTimeAppliedForUrl = '';
let initialVolumeAppliedForUrl = '';
let position = normalizePosition(initialPosition);
let size = normalizeSize(initialSize);
let resizeObserver;
let dragging = false;
let dragOffset = { x: 0, y: 0 };

$: expectedMeta = job.getMediaFileMeta() ?? { name: job.getFileName(), size: null, type: '' };
$: if (!mediaFile) {
  mediaFile = getTranscriptionMediaFile(job.getJobId());
}
$: updateMediaSource(mediaFile);
$: mismatch = getMediaFileMismatch(mediaFile, expectedMeta);
$: mediaKind = getMediaKind(mediaFile, expectedMeta);

// Seek requests are immutable events from the transcript view; track the id so
// Svelte reactivity does not replay the same seek on unrelated state changes.
$: if (seekRequest && mediaElement && mediaSourceUrl && seekRequest.id !== lastSeekRequestId) {
  lastSeekRequestId = seekRequest.id;
  mediaElement.currentTime = Math.max(0, seekRequest.time || 0);
  mediaElement.play().catch(() => {});
}

function normalizePosition(value) {
  if (!value || !Number.isFinite(value.x) || !Number.isFinite(value.y)) {
    return { x: 96, y: 120 };
  }

  return { x: Math.max(0, value.x), y: Math.max(0, value.y) };
}

function normalizeSize(value) {
  if (!value || !Number.isFinite(value.width) || !Number.isFinite(value.height)) {
    return null;
  }

  return {
    width: Math.max(280, Math.round(value.width)),
    height: Math.max(132, Math.round(value.height)),
  };
}

function getCurrentSize() {
  if (!playerElement) return size;

  const rect = playerElement.getBoundingClientRect();
  return { width: Math.round(rect.width), height: Math.round(rect.height) };
}

function rememberLayout() {
  size = getCurrentSize();
  dispatch('layoutchange', {
    position,
    size,
  });
}

function getPlayerStyle(playerPosition, playerSize) {
  const sizeDeclaration = playerSize ? ' width: ' + playerSize.width + 'px; height: ' + playerSize.height + 'px;' : '';
  return 'left: ' + playerPosition.x + 'px; top: ' + playerPosition.y + 'px;' + sizeDeclaration;
}

function getMediaKind(file, meta) {
  const type = file ? file.type : meta.type;
  const name = file ? file.name : meta.name;
  const lowerName = name.toLowerCase();

  if (type.startsWith('video/') || /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(lowerName)) {
    return 'video';
  }
  return 'audio';
}

function getMediaFileReference(file, meta) {
  const candidates = [file?.webkitRelativePath, file?.relativePath, file?.path, file?.name, meta?.name];

  return candidates.find((value) => typeof value === 'string' && value.trim() !== '') || 'unbekannt';
}

function getMediaFileUnavailableMessage() {
  return mediaPlayerTexts.FileUnavailableError.replace(
    '{fileReference}',
    getMediaFileReference(mediaFile, expectedMeta),
  );
}

function getMediaUnsupportedMessage() {
  return mediaPlayerTexts.UnsupportedFormatError.replace(
    '{fileReference}',
    getMediaFileReference(mediaFile, expectedMeta),
  );
}

async function canReadMediaFile(file) {
  if (!file || typeof file.slice !== 'function') return false;

  try {
    await file.slice(0, Math.min(file.size || 1, 1)).arrayBuffer();
    return true;
  } catch (_error) {
    return false;
  }
}

async function handleMediaError() {
  const erroredFile = mediaFile;
  const canReadFile = await canReadMediaFile(erroredFile);

  if (erroredFile !== mediaFile) return;

  mediaErrorMessage = canReadFile ? getMediaUnsupportedMessage() : getMediaFileUnavailableMessage();
}

function clearMediaError() {
  mediaErrorMessage = '';
}

function updateMediaSource(file) {
  // The browser can play local files only through an object URL. Revoke the old
  // one whenever the selected file changes so large media blobs are released.
  const nextUrl = file ? URL.createObjectURL(file) : '';
  if (mediaSourceUrl === nextUrl) return;
  if (mediaSourceUrl) {
    URL.revokeObjectURL(mediaSourceUrl);
  }
  mediaSourceUrl = nextUrl;
}

function attachFile(file) {
  if (!file) return;

  mediaFile = file;
  clearMediaError();
  setTranscriptionMediaFile(job.getJobId(), file);
}

function handleFileSelection(event) {
  const file = event.currentTarget.files[0];
  attachFile(file);
  event.currentTarget.value = '';
}

function handleDrop(event) {
  const file = event.dataTransfer?.files?.[0];
  attachFile(file);
}

function startDragging(event) {
  if (event.button !== 0) return;
  if (event.target.closest('button')) return;

  dragging = true;
  dragOffset = {
    x: event.clientX - position.x,
    y: event.clientY - position.y,
  };
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

function drag(event) {
  if (!dragging) return;

  // Keep the draggable header reachable even when the user drags toward an edge.
  const maxX = Math.max(0, window.innerWidth - 240);
  const maxY = Math.max(0, window.innerHeight - 120);
  position = {
    x: Math.min(maxX, Math.max(0, event.clientX - dragOffset.x)),
    y: Math.min(maxY, Math.max(0, event.clientY - dragOffset.y)),
  };
}

function stopDragging() {
  if (!dragging) return;

  dragging = false;
  rememberLayout();
}

function openFilePicker() {
  fileInput.click();
}

function emitTime() {
  if (!mediaElement) return;

  dispatch('timechange', {
    time: mediaElement.currentTime,
  });
}

function emitVolume() {
  if (!mediaElement) return;

  dispatch('volumechange', {
    volume: mediaElement.volume,
    muted: mediaElement.muted,
  });
}

function applyInitialMediaState() {
  if (!mediaElement || !mediaSourceUrl) return;

  if (initialVolumeAppliedForUrl !== mediaSourceUrl) {
    initialVolumeAppliedForUrl = mediaSourceUrl;
    const volume = Number.isFinite(initialVolume) ? Math.min(1, Math.max(0, initialVolume)) : 1;
    mediaElement.volume = volume;
    mediaElement.muted = initialMuted;
  }

  if (initialTimeAppliedForUrl !== mediaSourceUrl) {
    initialTimeAppliedForUrl = mediaSourceUrl;
    const time = Number.isFinite(initialTime) ? Math.max(0, initialTime) : 0;
    if (time > 0) {
      mediaElement.currentTime = time;
    }
  }
}

function handleMediaLoaded() {
  clearMediaError();
  applyInitialMediaState();
}

function closePlayer() {
  emitTime();
  emitVolume();
  rememberLayout();
  dispatch('close');
}

onMount(() => {
  if (typeof ResizeObserver === 'undefined' || !playerElement) return;

  resizeObserver = new ResizeObserver(() => rememberLayout());
  resizeObserver.observe(playerElement);

  return () => resizeObserver?.disconnect();
});

onDestroy(() => {
  resizeObserver?.disconnect();
  if (mediaSourceUrl) {
    URL.revokeObjectURL(mediaSourceUrl);
  }
});
</script>

<svelte:window on:pointermove={drag} on:pointerup={stopDragging} />

<section
  bind:this={playerElement}
  class="media-player"
  style={getPlayerStyle(position, size)}
  aria-label={mediaPlayerTexts.Title}
>
  <div class="player-header" on:pointerdown={startDragging}>
    <span>{mediaPlayerTexts.Title}</span>
    <button
      type="button"
      class="icon-button"
      on:pointerdown|stopPropagation
      on:click|stopPropagation={closePlayer}
      aria-label={uiTexts.app.close}
    >
      <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
        <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
      </svg>
    </button>
  </div>

  <div class="player-body">
    {#if mediaSourceUrl}
      {#if mismatch}
        <p class="media-warning">
          {mediaPlayerTexts.FileMismatchWarning}
        </p>
      {/if}
      {#if mediaErrorMessage}
        <p class="media-error">
          {mediaErrorMessage}
        </p>
      {/if}
      {#if mediaKind === 'video'}
        <video bind:this={mediaElement} src={mediaSourceUrl} controls on:loadedmetadata={handleMediaLoaded} on:timeupdate={emitTime} on:seeked={emitTime} on:volumechange={emitVolume} on:error={handleMediaError}>
          <track kind="captions" />
        </video>
      {:else}
        <audio bind:this={mediaElement} src={mediaSourceUrl} controls on:loadedmetadata={handleMediaLoaded} on:timeupdate={emitTime} on:seeked={emitTime} on:volumechange={emitVolume} on:error={handleMediaError}>
          <track kind="captions" />
        </audio>
      {/if}
      <button type="button" class="secondary attach-button" on:click={openFilePicker}>
        {mediaPlayerTexts.ReplaceFile}
      </button>
    {:else}
      <button type="button" class="drop-target" on:click={openFilePicker} on:dragover|preventDefault on:drop|preventDefault={handleDrop}>
        <span>{mediaPlayerTexts.AttachFile}</span>
        {#if expectedMeta.name}
          <small>{expectedMeta.name}</small>
        {/if}
      </button>
    {/if}
    <input bind:this={fileInput} type="file" accept="audio/*,video/*" on:change={handleFileSelection} hidden />
  </div>
</section>

<style>
  .media-player {
    position: fixed;
    z-index: 10000;
    width: min(520px, calc(100vw - 24px));
    min-width: 280px;
    min-height: 132px;
    max-width: calc(100vw - 24px);
    max-height: calc(100vh - 24px);
    background: #fff;
    border: 2px solid #000;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
    resize: both;
    overflow: auto;
  }

  .player-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 42px;
    padding: 6px 8px 6px 12px;
    color: #fff;
    background: #000;
    cursor: move;
    user-select: none;
  }

  .player-header span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
  }

  .icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    margin: 0;
    color: #fff;
    background: transparent;
    border: 0;
    cursor: pointer;
  }

  .player-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
  }

  video,
  audio {
    display: block;
    width: 100%;
  }

  video {
    max-height: min(58vh, 460px);
    background: #111;
  }

  .drop-target {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 132px;
    width: 100%;
    padding: 16px;
    margin: 0;
    color: #000;
    background: #f2f2f2;
    border: 2px dashed #777;
    cursor: pointer;
    text-align: center;
  }

  .drop-target span {
    font-weight: 700;
  }

  .drop-target small {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #555;
  }

  .media-warning {
    margin: 0;
    padding: 8px;
    background: #fff3cd;
    border-left: 4px solid #8a6d00;
    color: #332700;
    font-size: 0.9rem;
    text-align: left;
  }

  .media-error {
    margin: 0;
    padding: 8px;
    background: #fdecec;
    border-left: 4px solid #b00020;
    color: #5f0011;
    font-size: 0.9rem;
    text-align: left;
  }

  .attach-button {
    align-self: flex-start;
    width: auto;
    margin: 0;
  }
</style>
